import { useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { useRetentionSettings } from '@/features/retention/api';

/**
 * Media retention notice (FR-RET-001).
 *
 * Dismissible per session, not permanently: the business's exposure to "nobody told me my
 * photos would be deleted" disputes (BRD R-06) is exactly what this banner mitigates, so it
 * returns on the next sign-in.
 *
 * The wording comes from the same setting the PDF template prints (FR-RET-006), so what a
 * customer is told on screen and what is printed on their document cannot drift apart. The
 * constant below is only a fallback for the moment before that request resolves — never a
 * second copy of the policy.
 */

const SESSION_KEY = 'retention-banner-dismissed';

const FALLBACK_NOTICE =
  'Photographs and video footage are retained for a maximum of 12 months from the event end date. ' +
  'Customers should collect and back up their media before the retention expiry date shown on each event.';

export function RetentionBanner({ notice }: { notice?: string }) {
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(SESSION_KEY) === 'true');
  const { data: settings } = useRetentionSettings();

  if (dismissed) return null;

  const text = notice ?? settings?.noticeText ?? FALLBACK_NOTICE;

  return (
    <div role="status" className="border-b border-amber-200 bg-amber-50 px-3 py-2 sm:px-4">
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
        <p className="min-w-0 flex-1 text-xs text-amber-900 sm:text-sm">
          <span className="font-semibold">Media Retention Notice: </span>
          {text}
        </p>
        <button
          type="button"
          onClick={() => {
            sessionStorage.setItem(SESSION_KEY, 'true');
            setDismissed(true);
          }}
          className="shrink-0 rounded p-1 text-amber-700 transition-colors hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
          aria-label="Dismiss retention notice for this session"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
